// Full installed-chain inspection for our own write. Authenticated repo.getRecord responses are
// PDS reports, NOT MST proofs. Strong refs always compare against returned wire CIDs, never CIDs
// re-derived from arbitrary fetched records (the record encoders have different domains).
import { parseAtUri, readCidField, readLink, readStrongRef, recomputeRootCid } from "@szzt/records";
import type { MaslRecordValue } from "@szzt/records";
import { COLLECTION, recordCid } from "./records.ts";
import type { FetchedRecord, PdsClient } from "./types.ts";

interface ExpectedRecord {
  uri: string;
  cid: string;
  value: unknown;
  rkey: string;
}

export interface ExpectedPublish {
  manifest: ExpectedRecord;
  deployment: ExpectedRecord;
  site: ExpectedRecord;
  rootCid: string;
}

export interface PublishReadback {
  problem: string | null;
  superseded: boolean;
}

function identityProblem(record: FetchedRecord | null, expected: ExpectedRecord, collection: string): string | null {
  if (record === null) return `${collection} record absent immediately after write`;
  if (record.uri !== expected.uri || (record.value as { $type?: unknown } | null)?.$type !== collection) {
    return `${collection} record has the wrong URI or $type`;
  }
  if (record.cid !== expected.cid) return `${collection} wire CID does not match the written record's pin`;
  return null;
}

/** Values that WE authored use a known encoding domain. Equality here compares decoded content
 * only; computed CIDs are never used as proof or a replacement for a fetched wire CAS token. */
async function sameValue(record: FetchedRecord, expected: ExpectedRecord): Promise<boolean> {
  return await recordCid(record.value) === await recordCid(expected.value);
}

export async function inspectPublishedChain(client: PdsClient, expected: ExpectedPublish): Promise<PublishReadback> {
  try {
    const manifest = await client.getRecord(COLLECTION.masl, expected.manifest.rkey);
    const manifestProblem = identityProblem(manifest, expected.manifest, COLLECTION.masl);
    if (manifestProblem !== null) return { problem: manifestProblem, superseded: false };
    const root = readCidField(manifest!.value, "cid", COLLECTION.masl);
    if (root !== expected.rootCid ||
      await recomputeRootCid((manifest!.value as MaslRecordValue).tile) !== root ||
      !await sameValue(manifest!, expected.manifest)) {
      return { problem: "stored manifest content differs from the authored manifest/root", superseded: false };
    }

    const deployment = await client.getRecord(COLLECTION.deployment, expected.deployment.rkey);
    const deploymentProblem = identityProblem(deployment, expected.deployment, COLLECTION.deployment);
    if (deploymentProblem !== null) return { problem: deploymentProblem, superseded: false };
    const tile = readStrongRef(deployment!.value, "tile", COLLECTION.deployment);
    if (tile.uri !== manifest!.uri || tile.cid !== manifest!.cid ||
      readLink(deployment!.value, "root", COLLECTION.deployment) !== root ||
      !await sameValue(deployment!, expected.deployment)) {
      return { problem: "stored deployment content or manifest strong ref differs from the authored chain", superseded: false };
    }

    const site = await client.getRecord(COLLECTION.site, expected.site.rkey);
    if (site === null) return { problem: "site record absent immediately after write", superseded: false };
    if (site.uri !== expected.site.uri || (site.value as { $type?: unknown } | null)?.$type !== COLLECTION.site) {
      return { problem: "site record has the wrong URI or $type", superseded: false };
    }
    const current = readStrongRef(site.value, "current", COLLECTION.site);
    if (current.uri !== deployment!.uri) {
      const target = parseAtUri(current.uri);
      if (target.did !== client.did || target.collection !== COLLECTION.deployment) {
        return { problem: "changed site points outside its own deployment collection", superseded: false };
      }
      // Another publisher may legitimately move the mutable switch before our readback. Our
      // immutable records were checked above; do not reject or rewind their later deployment.
      return { problem: null, superseded: true };
    }
    const siteProblem = identityProblem(site, expected.site, COLLECTION.site);
    if (siteProblem !== null) return { problem: siteProblem, superseded: false };
    if (current.cid !== deployment!.cid || !await sameValue(site, expected.site)) {
      return { problem: "stored site content or deployment strong ref differs from the authored chain", superseded: false };
    }
    return { problem: null, superseded: false };
  } catch (err) {
    return {
      problem: `record-chain readback unavailable or malformed: ${err instanceof Error ? err.message : String(err)}`,
      superseded: false,
    };
  }
}
